<script setup lang="ts">
    import { computed, ref, watch, onMounted } from 'vue'
    import { useRoute } from 'vue-router'
    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import Navigation from '@common/Navigation.vue'
    import ProjectNavigation from '@Quest/ProjectPlans/Components/ProjectNavigation.vue'
    import ProjectContextHeader from '@Quest/ProjectPlans/Components/ProjectContextHeader.vue'
    import PnlViewer from '@Quest/Pnls/Components/PnlViewer.vue'
    import type { CurrencyDenomination } from '@common/types'
    import { useProjectPlanPnlQueryV1 } from '@Quest/ProjectPlans/queries-vue-composables'
    import { useUpdateBenefitsConfidenceFactorCommandV1 } from '@Quest/ProjectPlans/commands-vue-composables'
    import type { ProjectPnlReadModel } from '@Quest/ProjectPlans/readmodels'
    import type { PnlReportReadModel } from '@Quest/Pnls/readmodels'

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))
    const projectId = computed(() => String(route.params.projectId ?? ''))

    const projectPlanId = computed<string | null>(() => rm.value?.ProjectPlanId ?? null)
    const denomination = computed<CurrencyDenomination | null>(() => rm.value?.Denomination ?? null)

    const projectName = computed<string | null>(() => rm.value?.ProjectName ?? null)
    const projectPlanName = computed<string>(() => rm.value?.ProjectPlanName ?? '')
    const activePlanId = computed<string | null>(() => rm.value?.ActiveProjectPlanId ?? null)

    const applyBenefitsConfidenceFactor = computed<boolean>(() => rm.value?.ApplyBenefitsConfidenceFactor ?? false)
    const pnlId = computed<string | null>(() => rm.value?.PnlId ?? null)
    const report = computed<PnlReportReadModel | null>(() => rm.value?.Report ?? null)

    const portfolioId = computed(() => rm.value?.PortfolioId ?? null)
    const userId = computed(() => rm.value?.UserId ?? 0)
    const isWorkspaceMember = computed(() => rm.value?.IsWorkspaceMember ?? false)
    const isProjectOwner = computed(() => rm.value?.IsProjectOwner ?? false)
    const pnlTemplates = computed(() => rm.value?.PnlTemplates ?? [])

    const cagrVisible = ref(false)
    const benefitsConfidenceFactorInput = ref<number | null>(null)
    const savingBenefitsConfidenceFactorInput = ref(false)

    const {
        runProjectPlanPnlQueryV1,
        modelProjectPlanPnlQueryV1
    } = useProjectPlanPnlQueryV1(workspaceId, tchProjectId, projectId, projectPlanId, pnlId, applyBenefitsConfidenceFactor, denomination)

    const rm = ref<ProjectPnlReadModel | null>(null)


    const selectedPnl = computed(() =>
    {
        if (!pnlTemplates.value.length) return null

        return (
            (pnlId.value
                ? pnlTemplates.value.find(a => a.Id === pnlId.value)
                : pnlTemplates.value.find(a => a.IsDefault === true)
            ) ?? null
        )
    })

    const benefitsConfidenceFactorLabel = computed(() =>
    {
        const val = rm.value?.BenefitsConfidenceFactorValue
        return val != null ? `${val}%` : '100%'
    })


    function toggleBenefitsConfidenceFactor()
    {
        runProjectPlanPnlQueryV1({
            ApplyBenefitsConfidenceFactor: !applyBenefitsConfidenceFactor.value
        })
    }

    function switchPnlTemplate(id: string | null)
    {
        runProjectPlanPnlQueryV1({
            PnlId: id
        })
    }


    function toggleCagrVisible()
    {
        cagrVisible.value = !cagrVisible.value
    }



    function setDenomination(d: CurrencyDenomination)
    {
        runProjectPlanPnlQueryV1({
            Denomination: d
        })
    }

    const {
        invokeUpdateBenefitsConfidenceFactorCommandV1,
        onSucceedUpdateBenefitsConfidenceFactorCommandV1
    } = useUpdateBenefitsConfidenceFactorCommandV1(userId, workspaceId, portfolioId, projectId, projectPlanId, benefitsConfidenceFactorInput)

    function handleUpdateBenefitsConfidenceFactorCommand()
    {
        savingBenefitsConfidenceFactorInput.value = true
        invokeUpdateBenefitsConfidenceFactorCommandV1()
    }

    onMounted(() =>
    {
        document.title = 'Project P&L | TransparentChoice'

        runProjectPlanPnlQueryV1({
            ProjectPlanId: route.params.planId ? String(route.params.planId) : null,
            Denomination: route.query.denomination as CurrencyDenomination ?? null,
        })
    })

    watch(() => route.params.planId, () =>
    {
        runProjectPlanPnlQueryV1({
            ProjectPlanId: route.params.planId as string | null
        })
    })


    watch(modelProjectPlanPnlQueryV1, (current) =>
    {
        if (current?.RedirectUrl)
        {
            window.location.replace(current.RedirectUrl)
        }

        if (current)
        {
            rm.value = current
            benefitsConfidenceFactorInput.value = current.BenefitsConfidenceFactorValue
        }
    })

    onSucceedUpdateBenefitsConfidenceFactorCommandV1(() =>
    {
        savingBenefitsConfidenceFactorInput.value = false
        runProjectPlanPnlQueryV1()
    })

</script>

<template>
    <div>
        <PortfolioNavigation v-if="isWorkspaceMember" :workspaceId="workspaceId" :projectId="tchProjectId" />
        <Navigation v-else-if="isProjectOwner" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <ProjectContextHeader :projectName="projectName"
                                          :planId="projectPlanId"
                                          :planName="projectPlanName"
                                          :activePlanId="activePlanId" />
                </div>
                <div class="col-lg-6 text-right align-self-end">

                </div>
            </div>
            <hr />

            <ProjectNavigation ActivePage="pnl" class="mt-4 mb-4"
                               :workspaceId="workspaceId"
                               :tchProjectId="tchProjectId"
                               :projectId="projectId"
                               :planId="projectPlanId" />

            <div class="mb-3">



                <button type="button" class="mr-1" :class="cagrVisible ? 'btn btn-info' : 'btn btn-outline-secondary'" @click="toggleCagrVisible()">
                    <i :class="cagrVisible ? 'fa-regular fa-square-check' : 'fa-regular fa-square'"></i>
                    CAGR
                </button>

                <div class="btn-group mr-1">
                    <button type="button" :class="applyBenefitsConfidenceFactor ? 'btn btn-danger' : 'btn btn-outline-secondary'" @click="toggleBenefitsConfidenceFactor()">
                        <i :class="applyBenefitsConfidenceFactor ? 'fa-regular fa-square-check' : 'fa-regular fa-square'"></i>
                        Risk adjusted benefits ({{benefitsConfidenceFactorLabel}})
                    </button>
                    <button type="button" class="btn btn-outline-secondary dropdown-toggle-split rounded-right" data-toggle="dropdown" aria-expanded="false">
                        <i class="fa fa-pencil"></i>
                        <span class="sr-only">Toggle Dropdown</span>
                    </button>
                    <div class="dropdown-menu dropdown-menu-right p-2" style="width: 100%;" @click.stop="">
                        <form @submit.prevent="handleUpdateBenefitsConfidenceFactorCommand">
                            <div class="form-group">
                                <label>Benefits confidence factor (0-100):</label>
                                <input type="number" class="form-control" v-model.number="benefitsConfidenceFactorInput" placeholder="100" min="0" max="100" step="0.1"
                                       :disabled="savingBenefitsConfidenceFactorInput" />
                            </div>
                            <button type="submit" class="btn btn-primary" :disabled="savingBenefitsConfidenceFactorInput">Update</button>
                        </form>
                    </div>
                </div>

                <div class="btn-group mr-1">
                    <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown">
                        {{denomination}}s
                    </button>
                    <div class="dropdown-menu">
                        <a class="dropdown-item" @click="setDenomination('Million');">Millions</a>
                        <a class="dropdown-item" @click="setDenomination('Thousand');">Thousands</a>
                        <a class="dropdown-item" @click="setDenomination('Unit');">Units</a>
                    </div>
                </div>

                <div class="btn-group mr-1" v-if="pnlTemplates">
                    <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">P&L: {{selectedPnl?.Name}}</button>
                    <div class="dropdown-menu dropdown-menu-right">
                        <a class="dropdown-item" v-for="t in pnlTemplates" :key="t.Id" @click="switchPnlTemplate(t.Id)">{{t.Name}}</a>
                    </div>
                </div>

            </div>
            <PnlViewer :readModel="report" :cagrVisible="cagrVisible" />
        </div>
    </div>
</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }
</style>
